import { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, ArrowUpRight, SlidersHorizontal } from "lucide-react";
import { api } from "../lib/api";
import { MaskedLines, FadeUp } from "../components/Reveal";
import CoverImage from "../components/CoverImage";
import EntryActions from "../components/EntryActions";
import NoteDialog from "../components/NoteDialog";
import MultiSelect from "../components/MultiSelect";
import SortSelect from "../components/SortSelect";
import { useContent } from "../context/ContentContext";
import { secStyle, pageHeroClass, overlineColorClass, focalStyle } from "../lib/layout";
import { coverSrc, heroSrcSet } from "../lib/images";

const FIELD_ORDER = ["Philosophy", "Literature", "Science", "Mathematics", "Art & Film", "History & Society"];
const TYPE_LABEL = { book: "Book", essay: "Essay" };
const TYPE_OPTIONS = [{ value: "book", label: "Books" }, { value: "essay", label: "Essays" }];
const SORTS = [
  { key: "az", label: "Title A–Z" },
  { key: "za", label: "Title Z–A" },
  { key: "newest", label: "Publication date (newest)" },
  { key: "oldest", label: "Publication date (oldest)" },
];
const FORMS = [{ k: "", l: "All", slug: "all" }, { k: "Fiction", l: "Fiction", slug: "fiction" }, { k: "Non-fiction", l: "Non-fiction", slug: "nonfiction" }];

const PAGE = 40;

const matchesQuery = (i, q) => [i.title, i.creator, i.note, ...(i.tags || [])].join(" ").toLowerCase().includes(q);

// Gutter wayfinder label: the letter when sorting by title, the era when by date.
const markerFor = (item, sort) => {
  if (sort === "az" || sort === "za") return (item.title?.[0] || "#").toUpperCase();
  const y = item.year;
  if (!Number.isFinite(y)) return "Undated";
  if (y < 0) return `${Math.ceil(Math.abs(y) / 100) * 100}s BCE`;
  if (y < 1500) return `${Math.floor(y / 100) * 100}s`;
  return `${Math.floor(y / 10) * 10}s`;
};

const Chip = ({ label, onRemove, testId }) => (
  <button
    onClick={onRemove}
    data-testid={testId}
    className="group inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 border border-line text-vellum-muted hover:border-crimson hover:text-crimson transition-colors"
  >
    {label}
    <X className="w-3 h-3" />
  </button>
);

export default function Library() {
  const { content } = useContent();
  const libImg = coverSrc(content.pages?.library?.image, "hero");
  const libFocal = content.pages?.library?.focal;
  const libStyle = focalStyle(libFocal);
  const libHero = content.pages?.library || {};

  const [items, setItems] = useState([]);
  const [fieldsSel, setFieldsSel] = useState([]);
  const [typesSel, setTypesSel] = useState([]);
  const [themesSel, setThemesSel] = useState([]);
  const [formSel, setFormSel] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("oldest");
  const [noteItem, setNoteItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    api
      .get("/library")
      .then((r) => setItems(r.data.filter((i) => i.media_type === "book" || i.media_type === "essay")))
      .catch((e) => console.error("Could not load the library", e))
      .finally(() => setLoading(false));
  }, []);

  const fieldOptions = useMemo(() => {
    const present = new Set(items.map((i) => i.field).filter(Boolean));
    return FIELD_ORDER.filter((f) => present.has(f));
  }, [items]);

  // Theme options reflect current field/type selection
  const themeOptions = useMemo(() => {
    let list = items;
    if (fieldsSel.length) list = list.filter((i) => fieldsSel.includes(i.field));
    if (typesSel.length) list = list.filter((i) => typesSel.includes(i.media_type));
    return [...new Set(list.flatMap((i) => i.tags || []))].sort();
  }, [items, fieldsSel, typesSel]);

  // drop selected themes that are no longer available
  useEffect(() => {
    setThemesSel((sel) => sel.filter((t) => themeOptions.includes(t)));
  }, [themeOptions]);

  const filtered = useMemo(() => {
    let list = items;
    if (fieldsSel.length) list = list.filter((i) => fieldsSel.includes(i.field));
    if (typesSel.length) list = list.filter((i) => typesSel.includes(i.media_type));
    if (themesSel.length) list = list.filter((i) => (i.tags || []).some((t) => themesSel.includes(t)));
    if (formSel) list = list.filter((i) => i.form === formSel);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((i) => matchesQuery(i, q));
    const sorted = [...list];
    if (sort === "az") sorted.sort((a, b) => a.title.localeCompare(b.title));
    else if (sort === "za") sorted.sort((a, b) => b.title.localeCompare(a.title));
    else if (sort === "newest") sorted.sort((a, b) => (b.year || 0) - (a.year || 0));
    else if (sort === "oldest") sorted.sort((a, b) => (a.year || 0) - (b.year || 0));
    return sorted;
  }, [items, fieldsSel, typesSel, themesSel, formSel, query, sort]);

  // Render in chunks so the first screenful paints immediately even with
  // thousands of entries; the sentinel below the grid pulls in the next page.
  const [shown, setShown] = useState(PAGE);
  const sentinel = useRef(null);
  useEffect(() => { setShown(PAGE); }, [fieldsSel, typesSel, themesSel, formSel, query, sort]);
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) setShown((n) => n + PAGE); },
      { rootMargin: "800px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [filtered.length]);
  const visible = useMemo(() => filtered.slice(0, shown), [filtered, shown]);

  // Wayfinder: track which era/letter the reader is currently looking at.
  const gridRef = useRef(null);
  const [marker, setMarker] = useState("");
  useEffect(() => {
    const root = gridRef.current;
    if (!root) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        const top = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (top) setMarker(top.target.dataset.marker || "");
      },
      { rootMargin: "-190px 0px -68% 0px" },
    );
    root.querySelectorAll("[data-marker]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [visible, sort]);

  const activeCount = fieldsSel.length + typesSel.length + themesSel.length + (formSel ? 1 : 0);
  const hasActiveFilters = activeCount > 0 || query.trim() !== "";
  const clearAll = () => { setFieldsSel([]); setTypesSel([]); setThemesSel([]); setFormSel(""); setQuery(""); };

  return (
    <div data-testid="library-page">
      {/* Hero — artwork behind the title */}
      <section className={`relative overflow-hidden flex flex-col justify-end pt-16 lg:pt-20 ${pageHeroClass(content.pages?.library?.hero_height)}`} data-testid="library-hero">
        <div className="absolute inset-0 z-0">
          {libImg && <img src={libImg} srcSet={heroSrcSet(content.pages?.library?.image)} sizes="100vw" alt="" style={libStyle} fetchPriority="high" decoding="async" className="w-full h-full object-cover hero-img gpu-layer" />}
          <div className="absolute inset-0 hero-overlay" />
          {(libFocal?.veil ?? 0) > 0 && <div className="absolute inset-0 bg-black pointer-events-none" style={{ opacity: libFocal.veil / 100 }} />}
        </div>
        <div className="relative z-10 max-w-[1600px] mx-auto w-full px-6 lg:px-12 pt-6 pb-12 lg:pb-16 hero-text">
          <h1 className="font-serif font-light tracking-tighter leading-[0.88] text-vellum text-[clamp(3rem,11vw,9rem)]">
            <MaskedLines lines={[libHero.title_line1 || "The"]} />
            <MaskedLines lines={[libHero.title_line2 || "Library"]} delay={0.15} lineClassName="italic text-brass" />
          </h1>
          <FadeUp delay={0.2}>
            <p className="mt-8 max-w-xl text-vellum-muted text-lg leading-relaxed">
              {libHero.subtitle}
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <p className={`overline mt-8 ${overlineColorClass(libHero.overline_color)}`} data-testid="library-overline">{libHero.overline || "The Collection"}</p>
          </FadeUp>
        </div>
      </section>

      {/* Sticky reading-room toolbar: hairlines only, solid ink ground */}
      <div className="sticky top-16 lg:top-20 z-40 bg-ink border-y border-line" data-testid="library-toolbar">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          {/* Search + count */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 lg:gap-x-8 py-3">
            <div className="relative flex-1 min-w-0">
              <Search className="w-4 h-4 text-vellum-muted absolute left-0 top-1/2 -translate-y-1/2" />
              <input
                data-testid="library-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the library…"
                aria-label="Search the library"
                className="w-full bg-transparent border-b border-line pl-7 pr-7 py-2 text-vellum font-sans text-sm focus:border-brass focus:outline-none hover:border-vellum-muted transition-colors placeholder:text-vellum-muted"
              />
              {query && (
                <button onClick={() => setQuery("")} data-testid="library-search-clear" aria-label="Clear search" className="absolute right-0 top-1/2 -translate-y-1/2 text-vellum-muted hover:text-crimson transition-colors">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="order-3 w-full sm:order-none sm:w-auto shrink-0 font-mono text-[11px] uppercase tracking-[0.2em] text-vellum-muted" data-testid="library-count">
              {loading ? "Loading…" : `${filtered.length.toLocaleString()} ${filtered.length === 1 ? "entry" : "entries"}`}
            </p>
            <button
              onClick={() => setFiltersOpen((o) => !o)}
              data-testid="library-filters-toggle"
              aria-expanded={filtersOpen}
              className={`lg:hidden shrink-0 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] px-3 py-2 border transition-colors ${activeCount ? "border-brass text-brass" : "border-line text-vellum-muted"}`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              Filters{activeCount ? ` · ${activeCount}` : ""}
            </button>
          </div>

          {/* Filters row */}
          <div className={`${filtersOpen ? "flex" : "hidden"} lg:flex flex-wrap items-center gap-x-6 gap-y-3 pb-4 lg:pb-3 border-t border-line/60 pt-3 lg:border-t-0 lg:pt-0`}>
            <MultiSelect label="Field of Study" options={fieldOptions} selected={fieldsSel} onChange={setFieldsSel} testid="filter-field" />
            <MultiSelect label="Type of Work" options={TYPE_OPTIONS} selected={typesSel} onChange={setTypesSel} testid="filter-type" width="w-44" />
            <MultiSelect label="Themes" options={themeOptions} selected={themesSel} onChange={setThemesSel} testid="filter-themes" />
            <SortSelect label="Sort" options={SORTS} value={sort} onChange={setSort} testid="library-sort" width="w-60" />
            <div className="flex items-center gap-5" data-testid="filter-form">
              {FORMS.map((o) => (
                <button
                  key={o.slug}
                  onClick={() => setFormSel(o.k)}
                  data-testid={`filter-form-${o.slug}`}
                  className={`font-mono text-[11px] uppercase tracking-[0.2em] pb-1 border-b transition-colors ${
                    formSel === o.k ? "border-brass text-vellum" : "border-transparent text-vellum-muted hover:text-vellum"
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
            {hasActiveFilters && (
              <button onClick={clearAll} data-testid="library-clear" className="lg:ml-auto font-mono text-[11px] uppercase tracking-[0.18em] text-vellum-muted hover:text-crimson transition-colors">
                Clear all
              </button>
            )}
          </div>

          {/* Applied filters as removable chips */}
          {activeCount > 0 && (
            <div className="flex flex-wrap items-center gap-2 pb-3" data-testid="library-active-chips">
              {fieldsSel.map((f) => (
                <Chip key={`f-${f}`} label={f} testId={`chip-field-${f.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()}`} onRemove={() => setFieldsSel((s) => s.filter((x) => x !== f))} />
              ))}
              {typesSel.map((t) => (
                <Chip key={`t-${t}`} label={TYPE_LABEL[t] || t} testId={`chip-type-${t}`} onRemove={() => setTypesSel((s) => s.filter((x) => x !== t))} />
              ))}
              {themesSel.map((t) => (
                <Chip key={`th-${t}`} label={t} testId={`chip-theme-${t.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()}`} onRemove={() => setThemesSel((s) => s.filter((x) => x !== t))} />
              ))}
              {formSel && <Chip label={formSel} testId="chip-form" onRemove={() => setFormSel("")} />}
            </div>
          )}
        </div>
      </div>

      {/* Grid */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-10 pb-24" style={secStyle(content, "library_grid")}>
        {/* Gutter wayfinder */}
        {!loading && filtered.length > 24 && marker && (
          <div className="sticky top-40 lg:top-44 z-30 h-0 pointer-events-none hidden md:flex justify-end">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-vellum-muted bg-ink border border-line px-3 py-1.5" data-testid="library-wayfinder">
              {marker}
            </span>
          </div>
        )}

        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-x-5 gap-y-10 lg:gap-y-14" data-testid="library-skeletons">
            {Array.from({ length: 10 }).map((_, n) => (
              <div key={`sk-${n}`} className="flex flex-col">
                <div className="aspect-[2/3] border border-line hatch" />
                <div className="mt-3 h-3 w-3/4 bg-ink-800" />
                <div className="mt-2 h-3 w-1/2 bg-ink-800" />
              </div>
            ))}
          </div>
        )}

        <div ref={gridRef} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-x-5 gap-y-10 lg:gap-y-14">
          <AnimatePresence>
            {visible.map((item, idx) => (
              <motion.article
                key={item.id}
                data-testid={`library-item-${item.id}`}
                data-marker={markerFor(item, sort)}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: Math.min(0.24, (idx % 10) * 0.035) }}
                className="group flex flex-col"
              >
                <Link to={`/library/${item.id}`} className="block" tabIndex={-1} aria-hidden="true">
                  <div className="relative overflow-hidden border border-line mb-3 aspect-[2/3] bg-ink-800">
                    <CoverImage
                      src={item.cover_url}
                      alt={item.title}
                      focal={item.cover_focal}
                      testId={`cover-${item.id}`}
                      title={item.title}
                      creator={item.creator}
                      year={item.year}
                    />
                    {item.note && (
                      <div aria-hidden="true" className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-ink via-ink/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 hidden md:block">
                        <p className="text-xs italic text-vellum leading-snug line-clamp-4">{item.note}</p>
                      </div>
                    )}
                  </div>
                </Link>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-vellum-muted mb-1.5">
                  {TYPE_LABEL[item.media_type]}{item.form ? ` · ${item.form}` : ""}
                </p>
                <Link to={`/library/${item.id}`} className="block">
                  <h3 className="font-serif text-base lg:text-lg leading-tight text-vellum group-hover:text-brass transition-colors line-clamp-2">{item.title}</h3>
                </Link>
                <p className="font-mono text-[11px] text-vellum-muted mt-1 truncate">{item.creator}{item.year ? ` · ${item.year}` : ""}</p>
                <div className="mt-auto pt-3 flex items-center justify-between opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity duration-200">
                  <Link to={`/library/${item.id}`} data-testid={`view-${item.id}`} className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-muted hover:text-crimson transition-colors">
                    View <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                  <EntryActions item={item} onOpenNote={setNoteItem} />
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </div>
        <div ref={sentinel} aria-hidden="true" className="h-px" data-testid="library-sentinel" />
        {shown < filtered.length && (
          <p className="mt-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-vellum-muted" data-testid="library-loading-more">
            Loading more…
          </p>
        )}
        {!loading && filtered.length === 0 && (
          <div className="py-24 text-center">
            <p className="font-mono text-sm text-vellum-muted">No entries match your filters.</p>
            {hasActiveFilters ? <button onClick={clearAll} className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-crimson hover:underline">Clear all filters</button> : null}
          </div>
        )}
      </section>

      {noteItem && <NoteDialog item={noteItem} onClose={() => setNoteItem(null)} />}
    </div>
  );
}
